import React from 'react';
import { navigate, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
} from '../styles';
import { Link } from './ui';
import SaleBadge from './SaleBadge';
import ProductFillingRanges from './ProductFillingRanges';
import { ProductPrice } from './Prices';
import { buildUrl } from '../utils';
import bestSellerBadge from '../images/best-seller-badge.svg';
import antiAllergyBadge from '../images/anti-allergy-badge.png';

const StyledProductCard = styled.article`
  cursor: pointer;
`;

const StyledImageWrapper = styled.div`
  position: relative;

  ${({ isHuggleland }) => {
    if (isHuggleland) {
      return css`
        border: 5px solid ${brandColours.duodenary};
      `;
    }
  }}
`;

const StyledImage = styled(GatsbyImage)``;

const StyledBadge = styled.img`
  position: absolute;
  bottom: 8px;
  left: 8px;
  height: 80px;
  width: 80px;
  background-color: ${standardColours.white};
  border-radius: 50%;
`;

const StyledSaleBadge = styled(SaleBadge)`
  position: absolute;
  top: 8px;
  right: 8px;
  height: 80px;
  width: 80px;
  background-color: ${standardColours.white};
  border-radius: 50%;
`;

const StyledContent = styled.div`
  text-align: center;
`;

const StyledHeading = styled.h3`
  margin-top: 20px;
  color: ${standardColours.black};
  font-weight: ${fontWeights.semibold};
  ${fontSize(18)};

  ${minBreakpointQuery.small`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(22)};
  `}
`;

const StyledFillingRanges = styled(ProductFillingRanges)`
  justify-content: center;
  margin-top: 10px;
`;

const StyledPrice = styled(ProductPrice)`
  margin-top: 10px;
`;

const ProductCard = ({
  title,
  handle,
  collections,
  featuredImage,
  colour,
  filling,
  tags,
  priceRangeV2,
  variants,
  isBestSeller,
  isAntiAllergy,
  isInSale,
  isRetailProduct,
  ...props
}) => {
  const isHuggleland = collections.find(
    ({ handle }) => handle === 'huggleland'
  );

  return (
    <StyledProductCard onClick={() => navigate(buildUrl(handle))} {...props}>
      <StyledImageWrapper isHuggleland={isHuggleland}>
        <StyledImage
          image={featuredImage.gatsbyImageData}
          alt={featuredImage.altText || title}
        />
        {(isBestSeller || isAntiAllergy) && (
          <StyledBadge
            src={isBestSeller ? bestSellerBadge : antiAllergyBadge}
            alt={isBestSeller ? 'Best Seller' : 'Anti Allergy'}
            loading="lazy"
          />
        )}
        {isInSale && <StyledSaleBadge />}
      </StyledImageWrapper>
      <StyledContent>
        <StyledHeading>
          <Link to={handle}>{title}</Link>
        </StyledHeading>
        {!isHuggleland && (
          <StyledFillingRanges
            filling={filling}
            ranges={tags}
            isRetailProduct={
              isRetailProduct && JSON.parse(isRetailProduct.value)
            }
          />
        )}
        <StyledPrice
          priceRangeV2={priceRangeV2}
          variants={variants}
          isRetailProduct={isRetailProduct && JSON.parse(isRetailProduct.value)}
        />
      </StyledContent>
    </StyledProductCard>
  );
};

export default ProductCard;

export const ProductCardFragment = graphql`
  fragment ProductCardFragment on ShopifyProduct {
    id
    title
    handle
    collections {
      handle
    }
    featuredImage {
      gatsbyImageData(width: 480, height: 480)
      altText
    }
    colour: metafield(key: "colour", namespace: "custom") {
      value
    }
    coverMaterial: metafield(key: "cover_material", namespace: "my_fields") {
      value
    }
    filling: metafield(key: "filling", namespace: "my_fields") {
      value
    }
    weight: metafield(key: "weight", namespace: "my_fields") {
      value
    }
    size: metafield(key: "dimensions", namespace: "my_fields") {
      value
    }
    threadCount: metafield(key: "thread_count", namespace: "my_fields") {
      value
    }
    tags
    priceRangeV2 {
      minVariantPrice {
        amount
      }
      maxVariantPrice {
        amount
      }
    }
    variants {
      price
      compareAtPrice
    }
    isBestSeller: metafield(key: "best_seller", namespace: "my_fields") {
      value
    }
    isAntiAllergy: metafield(key: "anti_allergy", namespace: "my_fields") {
      value
    }
    isInSale: metafield(key: "in_sale", namespace: "my_fields") {
      value
    }
    isRetailProduct: metafield(key: "retail_product", namespace: "my_fields") {
      value
    }
    noIndex: metafield(key: "no_index", namespace: "custom") {
      value
    }
  }
`;
